import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Home, Search, PlusCircle, MessageSquare, Bell, User, LogOut, Shield } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const links = [
    { to: "/browse", label: "Browse", icon: Search, testid: "nav-browse" },
    { to: "/searches", label: "Searches", icon: Search, testid: "nav-searches" },
    { to: "/post", label: "Post", icon: PlusCircle, testid: "nav-post" },
    { to: "/enquiries", label: "Enquiries", icon: MessageSquare, testid: "nav-enquiries" },
    { to: "/chat", label: "Chat", icon: MessageSquare, testid: "nav-chat" },
  ];

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl brand-primary grid place-items-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-semibold text-lg text-stone-900">Flock</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {user && links.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition ${
                  isActive ? "text-stone-900 bg-stone-100" : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                }`
              }
            >
              <Icon className="w-4 h-4" /> {label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Button variant="ghost" size="icon" data-testid="nav-notifications" onClick={() => navigate("/notifications")}>
                <Bell className="w-5 h-5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="nav-user-menu" className="outline-none">
                  <Avatar className="w-9 h-9 border border-stone-200">
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback className="bg-stone-100 text-stone-700 text-sm">
                      {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-medium">{user.name || "User"}</div>
                    <div className="text-xs text-stone-500 truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
                    <User className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-my-listings" onClick={() => navigate("/my-listings")}>
                    <Home className="w-4 h-4 mr-2" /> My Listings
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-pricing" onClick={() => navigate("/pricing")}>
                    <Shield className="w-4 h-4 mr-2" /> Plans
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <Shield className="w-4 h-4 mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-login" onClick={() => navigate("/login")}>Login</Button>
              <Button data-testid="nav-signup" className="brand-accent rounded-full" onClick={() => navigate("/signup")}>
                Get started
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
